import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Calendar, Download, TrendingUp } from "lucide-react";

/** Weekly Sales Statement Ledger — mirrors the reference site's "Weekly Sales Statement Ledger".
 *  Reads from existing payouts + weekly-closing endpoints, no new backend surface required.
 */
export default function WeeklySales() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dist/payouts").catch(() => ({ data: [] })),
      api.get("/dist/overview").catch(() => ({ data: {} })),
    ]).then(([p, o]) => {
      // Build a summary week list from payouts (grouped by week window)
      const rows = (p.data || []).slice(0, 24).map((row, i) => ({
        week: row.week || `Week ${new Date(row.created_at || Date.now()).toLocaleDateString("en-GB")}`,
        left_sp:  row.left_sp  ?? row.left_sp  ?? 0,
        right_sp: row.right_sp ?? row.right_sp ?? 0,
        matched:  row.matched  ?? row.pairs    ?? 0,
        amount:   row.amount   ?? row.total    ?? 0,
        status:   row.status   || "credited",
      }));
      // Prepend current-week snapshot from overview (if no rows yet)
      if (!rows.length && o.data?.income) {
        rows.push({
          week: "Current Week",
          left_sp:  o.data.left_sp  || 0,
          right_sp: o.data.right_sp || 0,
          matched: 0,
          amount: o.data.income?.weekly || 0,
          status: "in-progress",
        });
      }
      setWeeks(rows);
      setLoading(false);
    });
  }, []);

  const totalPaid = weeks.reduce((s, w) => s + Number(w.amount || 0), 0);
  const totalLeft = weeks.reduce((s, w) => s + Number(w.left_sp || 0), 0);
  const totalRight = weeks.reduce((s, w) => s + Number(w.right_sp || 0), 0);

  return (
    <DashboardLayout title="Weekly Sales Statement Ledger" subtitle="Your week-by-week SP + payout history">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SummaryCard label="Total Left SP" value={totalLeft} tone="cyan" />
        <SummaryCard label="Total Right SP" value={totalRight} tone="pink" />
        <SummaryCard label="Total Paid" value={inr(totalPaid)} tone="green" />
      </div>

      <div className="bg-white border border-line rounded-sm">
        <div className="px-6 py-4 border-b border-line flex items-center gap-2">
          <TrendingUp size={16} className="text-gold-deep" />
          <div className="font-serif text-lg text-royal flex-1">Weekly ledger</div>
          <button data-testid="weekly-export" onClick={() => window.print()} className="text-xs text-royal hover:text-gold inline-flex items-center gap-1">
            <Download size={12} /> Export / Print
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ivory text-navy/60 border-b border-line">
                <th className="text-left px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Week</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Left SP</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Right SP</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Matched Pairs</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Amount</th>
                <th className="text-center px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8 text-navy/50">Loading…</td></tr>
              ) : weeks.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-navy/50">No weekly ledger data yet. Your first pair credit will appear here.</td></tr>
              ) : weeks.map((w, i) => (
                <tr key={i} data-testid={`weekly-row-${i}`} className="border-b border-line last:border-0 hover:bg-ivory/50">
                  <td className="px-6 py-3 font-serif text-royal">
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-gold" />
                      {w.week}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums">{Number(w.left_sp).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{Number(w.right_sp).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{Number(w.matched).toLocaleString("en-IN")}</td>
                  <td className="px-6 py-3 text-right tabular-nums font-medium text-gold-deep">{inr(w.amount)}</td>
                  <td className="px-6 py-3 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                      w.status === "credited" ? "bg-olive/10 text-olive border-olive/20"
                      : w.status === "in-progress" ? "bg-gold/10 text-gold-deep border-gold/25"
                      : "bg-navy/5 text-navy/60 border-line"
                    }`}>{w.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, tone }) {
  const tones = {
    cyan:  "border-cyan-500/30 bg-cyan-500/[0.05] text-cyan-800",
    pink:  "border-rose/30 bg-rose/[0.05] text-rose",
    green: "border-olive/30 bg-olive/[0.05] text-olive",
  };
  return (
    <div data-testid={`weekly-summary-${label.toLowerCase().replace(/ /g,'-')}`} className={`p-5 border-2 rounded-sm ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">{label}</div>
      <div className="font-serif text-3xl mt-2 text-royal">{value}</div>
    </div>
  );
}
